import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { createLogResolver } from '@core/logs/resolvers/create-log.resolver';
import { ROUTE_GUARDS } from '@core/routing/route-guards';
import { MODERN_FRAME_MATCHER_ROUTE, MODERN_FRAME_ROOT_REDIRECTS } from '@core/routing/modern-frame-routing';

import { LoginComponent } from '../app/pages/login/login.component';
import { AccessDeniedComponent } from './components/access-denied/access-denied.component';
import { UrlNotFoundComponent } from './components/url-not-found/url-not-found.component';
import { environment } from '../environments/environment';

const routes: Routes = [
  {
    path: '',
    component: LoginComponent,
    pathMatch: 'full'
  },
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'home',
    loadComponent: () => import('../app/home/pages/home/home.component').then((m) => m.HomeComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      pagePath: 'home'
    }
  },
  /** Modern microfront — disabled in production until real applications ship */
  ...(environment.modernFrameRoutesEnabled
    ? [...MODERN_FRAME_ROOT_REDIRECTS, MODERN_FRAME_MATCHER_ROUTE]
    : []),
  {
    path: 'teams-setup',
    loadComponent: () => import('./pages/request-setup/request-setup.component').then((m) => m.RequestSetupComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Teams Setup',
      pagePath: 'teams-setup'
    }
  },
  {
    path: 'users-setup',
    loadComponent: () => import('./pages/request-setup/request-setup.component').then((m) => m.RequestSetupComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Users Setup',
      pagePath: 'users-setup'
    }
  },
  {
    path: 'roles-setup',
    loadComponent: () => import('./pages/request-setup/request-setup.component').then((m) => m.RequestSetupComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Roles Setup',
      pagePath: 'roles-setup'
    }
  },
  {
    path: 'task-management/request-setup',
    loadComponent: () => import('./pages/request-setup/request-setup.component').then((m) => m.RequestSetupComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Request Type Setup',
      pagePath: 'task-management/request-setup'
    }
  },
  {
    path: 'task-management/priority-setup',
    loadComponent: () => import('./pages/request-setup/request-setup.component').then((m) => m.RequestSetupComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Priority Setup',
      pagePath: 'task-management/priority-setup'
    }
  },
  /* Skills Setup */
  {
    path: 'skills-management/skill-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/skills-center/skills-center.component').then((m) => m.SkillsCenterComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Skill Setup',
      pagePath: 'skills-management/skill-setup'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  /* Service Setup */
  {
    path: 'services-management/service-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/services-center/services-center.component').then((m) => m.ServicesCenterComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Service Setup',
      pagePath: 'services-management/service-setup'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  /* Technology Setup */
  {
    path: 'technologies-management/technology-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/technologies-center/technologies-center.component').then(
        (m) => m.TechnologiesCenterComponent
      ),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Technology Setup',
      pagePath: 'technologies-management/technology-setup'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  /* Certifications Setup */
  {
    path: 'certifications-management/certification-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/certifications-center/certifications-center.component').then(
        (m) => m.CertificationsCenterComponent
      ),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Certification Setup',
      pagePath: 'certifications-management/certification-setup'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  {
    path: 'vendors-management/vendor-setup',
    loadComponent: () =>
      import('@vendor-svm/pages/vendors-center/vendors-center.component').then((m) => m.VendorsCenterComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Vendor Setup',
      pagePath: 'vendors-management/vendor-setup'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  {
    path: 'vendors-management/vendor-dashboard',
    loadComponent: () =>
      import('@vendor-svm/pages/vendors-dashboard/vendors-dashboard.component').then(
        (m) => m.VendorsDashboardComponent
      ),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Vendor Dashboard',
      pagePath: 'vendors-management/vendor-dashboard'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  /** S2P */
  {
    path: 's2p',
    loadChildren: () => import('@s2p/routes').then((m) => m.S2P_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** My Workspace */
  {
    path: 'my-workspace',
    loadComponent: () =>
      import('@my-workspace/pages/my-workspace/my-workspace.component').then((m) => m.MyWorkspaceComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: { skip: true },
      pagePath: 'my-workspace'
    },
    loadChildren: () => import('@my-workspace/routes').then((m) => m.MY_WORKSPACE_ROUTES)
  },
  /** PO Backlog Management */
  {
    path: 'po-backlog-management',
    loadChildren: () => import('@po-backlog-management/routes').then((m) => m.PO_BACKLOG_MGMT_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** PO Backlog Dashboards */
  {
    path: 'po-snapshot-dashboard',
    loadComponent: () =>
      import('@po-backlog/po-snapshot-dashboard/pages/po-snapshot-dashboard/po-snapshot-dashboard.component').then(
        (m) => m.PoSnapshotDashboardComponent
      ),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'PO Snapshot Dashboard',
      pagePath: 'po-snapshot-dashboard'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  {
    path: 'po-backlog-dashboard',
    loadComponent: () =>
      import('@po-backlog/po-backlog-dashboard/pages/po-backlog-dashboard/po-backlog-dashboard.component').then(
        (m) => m.PoBacklogDashboardComponent
      ),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'PO Backlog Dashboard',
      pagePath: 'po-backlog-dashboard'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  /** Ship Preparation */
  {
    path: 'ship-preparation-management',
    loadChildren: () => import('@ship-preparation-management/routes').then((m) => m.SHIP_PREP_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** Renewals */
  {
    path: 'client-renewals-setup',
    loadComponent: () =>
      import('@renewals/renewals-setup/pages/renewals-setup-center/renewals-setup-center.component').then(
        (m) => m.RenewalsSetupCenterComponent
      ),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Client Renewals Setup',
      pagePath: 'client-renewals-setup'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  {
    path: 'renewals-new-quote',
    loadComponent: () =>
      import('@renewals/renewals-new-quote/pages/renewals-new-quote/renewals-new-quote.component').then(
        (m) => m.RenewalsNewQuoteComponent
      ),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Request a Quote',
      pagePath: 'renewals-new-quote'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  {
    path: 'renewals-management',
    loadChildren: () => import('@renewals-management/routes').then((m) => m.RENEWALS_MGMT_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** Super Client */
  {
    path: 'super-clients',
    loadChildren: () => import('@super-clients/routes').then((m) => m.SUPER_CLIENT_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** RMA */
  {
    path: 'rma',
    loadChildren: () => import('./task-management/rma/routes').then((m) => m.RMA_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** SaaS Auto-Renewals */
  {
    path: 'saas-renewals',
    loadChildren: () => import('./task-management/saas-renewals/routes').then((m) => m.SAAS_REN_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** Requests Management */
  {
    path: 'requests-management',
    loadComponent: () =>
      import('@requests-management/pages/requests-mgmt-center/requests-mgmt-center.component').then(
        (m) => m.RequestsMgmtCenterComponent
      ),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Requests Management',
      pagePath: 'requests-management'
    },
    resolve: {
      logCreated: createLogResolver()
    }
  },
  /** TS Project Management */
  {
    path: 'ts-project',
    loadChildren: () => import('./task-management/ts-project/routes').then((m) => m.TS_PROJECT_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  /** One Front Door */
  {
    path: 'one-front-door',
    loadChildren: () => import('./task-management/one-front-door/routes').then((m) => m.ONE_FRONT_DOOR_ROUTES),
    data: { breadcrumb: { skip: true } }
  },
  /** Contract Portal */
  {
    path: 'contract-portal',
    loadChildren: () => import('@contract-portal/routes').then((m) => m.CONTRACT_PORTAL_ROUTES),
    data: {
      breadcrumb: { skip: true }
    }
  },
  {
    path: 'menu-config',
    loadComponent: () => import('./pages/menu-config/menu-config.component').then((m) => m.MenuConfigComponent),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: 'Menu Config',
      pagePath: 'menu-config'
    }
  },
  /** Dev — Tailwind / new-ui isolation smoke test */
  {
    path: 'tailwind-test',
    loadComponent: () => import('./pages/tailwind-test/tailwind-test.component').then((m) => m.TailwindTestComponent),
    data: { newUI: true }
  },
  /** Megaboard — QA only */
  ...(environment.megaboardEnabled
    ? [
        {
          path: 'megaboard',
          loadChildren: () => import('./megaboard/megaboard.routes').then((m) => m.MEGABOARD_ROUTES),
          data: { breadcrumb: { skip: true } }
        }
      ]
    : []),
  {
    path: 'admin-setup',
    loadChildren: () => import('./admin-setup/routes').then((m) => m.ADMIN_SETUP_ROUTES),
    data: { newUI: true, breadcrumb: { skip: true } }
  },
  {
    path: 'jira-project',
    loadChildren: () => import('./jira-project/routes').then((m) => m.JIRA_PROJECT_ROUTES),
    canActivate: ROUTE_GUARDS,
    data: {
      breadcrumb: { skip: true }
    }
  },
  {
    path: 'access-denied',
    component: AccessDeniedComponent,
    data: { breadcrumb: 'Access Denied' }
  },
  {
    path: 'not-found',
    component: UrlNotFoundComponent
  },
  {
    path: '**',
    redirectTo: 'not-found'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { anchorScrolling: 'enabled', paramsInheritanceStrategy: 'always' })],
  exports: [RouterModule]
})
export class AppRoutingModule {}
